import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, Ban, Download, ExternalLink, UserCheck, Users, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  accepted: 'Matching', interest_open: 'Open for interest', admin_selecting: 'Selecting',
  expert_selected: 'Innovator selected', proposal_submitted: 'Proposal submitted',
  payment_received: 'Payment received', in_progress: 'In progress', completed: 'Completed',
  drafting: 'Drafting', proposal_ready: 'Brief ready',
};

export const ModuleDisabledNotice = ({ module, testId }) => (
  <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid={testId || `module-disabled-${module}`}>
    <Ban className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
    <div>
      <p className="text-sm font-medium text-amber-800">The {module} module is disabled</p>
      <p className="text-xs text-amber-700 mt-0.5">CollabHive has turned this module off for your university. You can still view this section, but actions are unavailable. Contact CollabHive to re-enable it.</p>
    </div>
  </div>
);

const StatusPill = ({ status }) => <Badge variant="secondary" className="text-[10px] shrink-0">{STATUS_LABEL[status] || status}</Badge>;
const Empty = ({ text }) => <p className="text-sm text-muted-foreground py-8 text-center">{text}</p>;

function useUniFetch(path, token) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    setData(null);
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    setData(res.ok ? await res.json() : {});
  }, [path, token]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

function ProjectsInvolved({ data }) {
  const rows = data.projects || [];
  if (!rows.length) return <Empty text="None of your innovators are involved in a project yet." />;
  return rows.map(p => (
    <Card key={p.id} data-testid={`involved-project-${p.id}`}>
      <CardContent className="p-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-sm">{p.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {p.selected_expert_name ? `Innovator: ${p.selected_expert_name}` : `${p.notified_count} innovator(s) notified`}
            {p.is_referral && ' · Referral industry'}
          </p>
        </div>
        <StatusPill status={p.status} />
      </CardContent>
    </Card>
  ));
}

function ProjectsNotified({ data }) {
  const rows = data.projects || [];
  if (!rows.length) return <Empty text="No projects have been notified to your innovators yet." />;
  return rows.map(p => (
    <Card key={p.id} data-testid={`notified-project-${p.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3">
          <p className="font-medium text-sm">{p.title}</p>
          <StatusPill status={p.status} />
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {p.experts.map(e => <Badge key={e.id} variant="outline" className="text-[10px]" title={e.email}>{e.name}</Badge>)}
        </div>
      </CardContent>
    </Card>
  ));
}

function Submissions({ data, token }) {
  const rows = data.submissions || [];
  const download = async (file) => {
    const res = await fetch(`${API}/university/files/${file.file_id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error('Download failed'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = file.filename || 'submission.pdf'; a.click();
    URL.revokeObjectURL(url);
  };
  if (!rows.length) return <Empty text="No submissions from your innovators yet." />;
  return rows.map(s => (
    <Card key={s.id} data-testid={`submission-${s.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-sm">{s.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {s.expert_name}{s.submission_number ? ` · Milestone ${s.submission_number}` : ''} · {(s.submitted_at || '').slice(0, 10)}
            </p>
          </div>
          <Badge className={s.kind === 'project' ? 'bg-primary/10 text-primary text-[10px]' : 'bg-blue-500/10 text-blue-700 text-[10px]'}>{s.kind === 'project' ? 'Project' : 'Opportunity'}</Badge>
        </div>
        {s.notes && <p className="text-xs text-muted-foreground mt-2 italic">“{s.notes}”</p>}
        <div className="flex flex-wrap gap-2 mt-2">
          {s.file?.file_id && <Button size="sm" variant="outline" className="gap-1 h-7 text-xs" onClick={() => download(s.file)} data-testid={`download-submission-${s.id}`}><Download className="w-3 h-3" /> {s.file.filename || 'File'}</Button>}
          {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid={`submission-link-${s.id}`}><ExternalLink className="w-3 h-3" /> Open link</a>}
        </div>
      </CardContent>
    </Card>
  ));
}

function Opportunities({ data, modules }) {
  const rows = data.opportunities || [];
  return (
    <>
      {!modules.opportunities && <ModuleDisabledNotice module="Opportunities" />}
      {!rows.length ? <Empty text="No opportunities have been notified to your innovators yet." /> : rows.map(o => (
        <Card key={o.id} data-testid={`uni-opportunity-${o.id}`}>
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-sm">{o.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
                  {o.sponsor_institution} · <Calendar className="w-3 h-3" /> {o.deadline}
                </p>
              </div>
              <Badge variant="secondary" className="text-[10px] shrink-0">{o.is_active ? 'Open' : 'Closed'}</Badge>
            </div>
            <p className="text-xs mt-2 flex items-center gap-1"><Users className="w-3 h-3" /> {o.experts.length} notified · {o.submissions_count} submitted</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {o.experts.map(e => <Badge key={e.id} variant="outline" className="text-[10px]">{e.name}</Badge>)}
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

function InnovatorSelection({ data, token, modules, reload, onChanged }) {
  const rows = data.projects || [];
  const [busy, setBusy] = useState(null);
  const select = async (projectId, expertId) => {
    if (!window.confirm('Select this innovator for the project? Other interested innovators will be marked as not selected.')) return;
    setBusy(expertId);
    try {
      const res = await fetch(`${API}/projects/university/${projectId}/select-expert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ expert_id: expertId }),
      });
      if (res.ok) { toast.success('Innovator selected. Industry has been notified.'); reload(); onChanged?.(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
    } finally { setBusy(null); }
  };
  return (
    <>
      {!modules.projects && <ModuleDisabledNotice module="Projects" />}
      {!rows.length ? <Empty text="No projects from your referred industry partners need an innovator yet." /> : rows.map(p => (
        <Card key={p.id} data-testid={`selection-project-${p.id}`}>
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-sm">{p.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{p.industry_user_email}{p.selected_expert_name && ` · Selected: ${p.selected_expert_name}`}</p>
              </div>
              <StatusPill status={p.status} />
            </div>
            <div className="mt-3 space-y-2">
              {!p.interests.length ? <p className="text-xs text-muted-foreground">No innovator has expressed interest yet.</p> : p.interests.map(i => (
                <div key={i.expert_id} className={`border rounded-md p-2 flex items-start justify-between gap-2 ${i.status === 'selected' ? 'border-primary/40 bg-primary/5' : ''}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{i.expert_name}</p>
                    <p className="text-[11px] text-muted-foreground">{i.expert_email}</p>
                    {i.notes && <p className="text-xs italic text-muted-foreground mt-1">“{i.notes}”</p>}
                  </div>
                  {i.status === 'selected' ? (
                    <Badge className="bg-primary text-primary-foreground text-[10px] gap-1 shrink-0" data-testid={`uni-selected-badge-${p.id}`}><UserCheck className="w-3 h-3" /> Selected</Badge>
                  ) : p.selection_open && modules.projects && (
                    <Button size="sm" className="h-7 text-xs shrink-0" disabled={busy === i.expert_id} onClick={() => select(p.id, i.expert_id)} data-testid={`uni-select-expert-${p.id}-${i.expert_id}`}>
                      {busy === i.expert_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Select'}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

const DIALOGS = {
  involved: { path: '/university/overview/projects-involved', title: 'Projects Involved', desc: 'Projects your innovators are part of, with their current status', Body: ProjectsInvolved },
  notified: { path: '/university/overview/projects-notified', title: 'Projects Notified', desc: 'Which of your innovators were notified about each project', Body: ProjectsNotified },
  submissions: { path: '/university/overview/submissions', title: 'Submissions Received', desc: 'Project milestones and opportunity proposals submitted by your innovators', Body: Submissions },
  opportunities: { path: '/university/opportunities', title: 'Opportunities', desc: 'Opportunities notified to your innovators', Body: Opportunities },
  selection: { path: '/university/innovator-selection', title: 'Innovators Selection', desc: 'Pick the innovator for projects raised by your referred industry partners', Body: InnovatorSelection },
};

function DialogBody({ kind, token, modules, onChanged }) {
  const cfg = DIALOGS[kind];
  const [data, reload] = useUniFetch(cfg.path, token);
  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return <div className="space-y-2"><cfg.Body data={data} token={token} modules={modules} reload={reload} onChanged={onChanged} /></div>;
}

export function UniversityOverviewDialog({ kind, onClose, token, modules, onChanged }) {
  const cfg = DIALOGS[kind];
  return (
    <Dialog open={!!kind} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={kind ? `uni-dialog-${kind}` : undefined}>
        {cfg && (
          <>
            <DialogHeader>
              <DialogTitle>{cfg.title}</DialogTitle>
              <DialogDescription>{cfg.desc}</DialogDescription>
            </DialogHeader>
            <DialogBody kind={kind} token={token} modules={modules} onChanged={onChanged} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
